/**
 * Realtime channel contract — the WebSocket protocol shared by the
 * dashboard server (`RealtimeHub` + the `/api/realtime` route) and the web
 * client (`realtimeSocket`). Single source of truth so the two sides can
 * never disagree about message shapes, the status enum, or presence
 * payloads.
 *
 * ONE always-open connection carries three things:
 *   1. **live notification push** — the server fans new events straight to
 *      the socket the instant the dispatcher detects them (the desktop popup
 *      / in-app snackbar consume the same events the SSE stream used to).
 *   2. **presence** — who is currently online.
 *   3. **user status** — `active` / `away` / `dnd`, which the user sets from
 *      the profile dropdown or the desktop tray. `dnd` gates the live push
 *      server-side: no popup / toast / sound, while the bell still fills.
 *
 * Status is deliberately split across TWO client frames, because a user has
 * many connections (a browser tab AND the desktop app) and they must not fight
 * each other:
 *   - `status` is the user's EXPLICIT pick — user-level, any device sets it
 *     for all of them;
 *   - `activity` is per-CONNECTION idleness, which the server aggregates
 *     ("active if ANY device is active") to derive auto-away.
 * The `hello` / `status` server frames always carry the resulting EFFECTIVE
 * status, which is what teammates see.
 *
 * The SSE stream (`/api/notifications/stream`) stays alive in parallel so
 * the already-installed desktop app keeps receiving notifications until it
 * updates to a WebSocket build — see docs/notifications.md.
 */
import {
  CHAT_ROOM_ALL,
  isChatMessage,
  type ChatMessage,
  type ChatReactionSummary,
  type ChatRoomId,
  type ChatScope,
} from "./chat.js";
import type { NotificationEvent } from "./notifications.js";
import {
  CALL_STATES,
  CALL_END_REASONS,
  type CallInvitePayload,
  type CallScreenPayload,
  type CallCameraPayload,
  type CallRosterParticipant,
  type CallState,
  type CallEndReason,
  type CallRoomCoords,
} from "./calls.js";

/** A user's chosen presence status. */
export const PRESENCE_STATUSES = ["active", "away", "dnd"] as const;
export type PresenceStatus = (typeof PRESENCE_STATUSES)[number];

/** Human-facing label for each status (profile dropdown, tray, tooltips). */
export const PRESENCE_STATUS_LABELS: Record<PresenceStatus, string> = {
  active: "Active",
  away: "Away",
  dnd: "Do Not Disturb",
};

/** Runtime guard — also used to validate untrusted wire values. */
export function isPresenceStatus(v: unknown): v is PresenceStatus {
  return (
    typeof v === "string" && (PRESENCE_STATUSES as readonly string[]).includes(v)
  );
}

/** One online user, as carried in presence payloads. */
export interface PresenceEntry {
  user_id: number;
  status: PresenceStatus;
}

/** Messages the client sends to the server. */
export type RealtimeClientMessage =
  /** The user's EXPLICIT status pick (profile dropdown / desktop tray). It is
   * a USER-level intent — choosing "Do Not Disturb" on one device means DND
   * everywhere — so the server stores it per user. Auto-away must NEVER be
   * sent this way; use `activity`. */
  | { t: "status"; status: PresenceStatus }
  /**
   * Whether THIS connection currently sees the user as idle. Per-CONNECTION,
   * never per-user: a laptop's tray-resident desktop app going idle must not
   * mark you away while you're actively working in a browser tab (that
   * last-writer-wins stomp was the "web login shows offline on desktop" bug).
   * The server aggregates — you are `active` while ANY connection reports
   * `idle: false`. Sent on every idle↔active transition and re-announced on
   * reconnect. On the desktop shell "idle" means idle **on the whole
   * computer** (see `systemIdle.ts`), not just inside our window.
   */
  | { t: "activity"; idle: boolean }
  /** "I'm typing in this conversation" — `to` is "all" (the room) or a
   * recipient user id. Ephemeral, never persisted; the server fans a
   * `chat_typing` to the right audience. */
  | { t: "chat_typing"; to: ChatRoomId | number }
  | { t: "ping" };

/** Messages the server sends to the client. */
export type RealtimeServerMessage =
  /** First frame after the socket authenticates: the caller's own id +
   * status (restored if they reconnected) and the current online set. */
  | { t: "hello"; user_id: number; status: PresenceStatus; online: PresenceEntry[] }
  /** A new notification (same payload the SSE stream delivers). */
  | { t: "notification"; event: NotificationEvent }
  /** A new chat message (DM or "All Staff" room) — pushed to the recipient
   * (and echoed to the sender's other connections). Mutations are REST; this
   * frame is purely the live-delivery side. See ./chat.ts + docs/chat.md. */
  | { t: "chat_message"; message: ChatMessage }
  /** An existing message was edited or deleted — carries the full updated
   * message (a delete arrives already blanked into a tombstone). The client
   * REPLACES the message in the cached thread by id. Routed like a message. */
  | { t: "chat_message_update"; message: ChatMessage }
  /** Someone is typing. Recipients derive the conversation: a `room` scope →
   * `room:all`; a `dm` scope → `dm:<from>`. Ephemeral (auto-expires client-
   * side after a couple seconds). */
  | { t: "chat_typing"; from: number; sender_name: string; scope: ChatScope; room: ChatRoomId | null }
  /** A DM read receipt: `reader_id` has read everything up to `last_read_id`
   * in their conversation with the recipient of this frame. Drives the
   * sender's sent→read tick flip. DM only (the room has too many readers). */
  | { t: "chat_read"; reader_id: number; last_read_id: number }
  /** A message's emoji reactions changed. Carries the message's routing
   * (scope/room/sender/recipient) so the client can place it in the right
   * conversation, plus the full new aggregated reaction set for that message.
   * Fans to the conversation the same way a `chat_message` does. */
  | {
      t: "chat_reaction";
      message_id: number;
      scope: ChatScope;
      room: ChatRoomId | null;
      sender_id: number;
      recipient_id: number | null;
      reactions: ChatReactionSummary[];
    }
  /** Echo / confirmation of the caller's own status after a change. */
  | { t: "status"; status: PresenceStatus }
  /** The online set changed (someone connected, left, or changed status). */
  | { t: "presence"; online: PresenceEntry[] }
  /** Heartbeat reply. */
  | { t: "pong" }
  /** Inbound call invite — pushed to the callee the instant a call starts.
   * Drives the incoming-call notification + UI ring. */
  | { t: "call_invite"; call: CallInvitePayload }
  /** Call state transition — pushed to both parties for every state change.
   * `reason` is set when `state === "ended"`; `room` is set when
   * `state === "connecting"` or `"connected"`. */
  | { t: "call_state"; call_id: string; state: CallState; reason?: CallEndReason; room?: CallRoomCoords }
  /** Screen-share signaling — pushed to the OTHER party when one participant
   * starts or stops sharing their screen. Phase 3 Track A. */
  | { t: "call_screen"; call_id: string; sharing: boolean; sharer_session_id: number }
  /** Camera signaling — pushed to the OTHER party when one participant turns
   * their camera on or off (audio-first 1:1 calls). Mirrors `call_screen`. */
  | { t: "call_camera"; call_id: string; sharing: boolean; sharer_session_id: number }
  /** Participant roster for a (group) call — broadcast to every participant when
   * someone is invited, joins, or leaves. Phase 11 (group calls). */
  | { t: "call_roster"; call_id: string; participants: CallRosterParticipant[] };

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function isReactionSummaryArray(v: unknown): v is ChatReactionSummary[] {
  return (
    Array.isArray(v) &&
    v.every(
      (e) =>
        isRecord(e) &&
        typeof e.emoji === "string" &&
        typeof e.count === "number" &&
        Array.isArray(e.user_ids) &&
        // Optional (older payloads omit it); when present it must be an array
        // so the tooltip can index it alongside user_ids.
        (e.user_names === undefined || Array.isArray(e.user_names)),
    )
  );
}

function isPresenceEntryArray(v: unknown): v is PresenceEntry[] {
  return (
    Array.isArray(v) &&
    v.every(
      (e) => isRecord(e) && typeof e.user_id === "number" && isPresenceStatus(e.status),
    )
  );
}

/**
 * Validate + narrow an untrusted client→server frame (used by the WS route).
 * Returns `null` for malformed or unrecognised input so the caller can drop
 * it without trusting any field.
 */
export function parseClientMessage(raw: string): RealtimeClientMessage | null {
  const v = safeJson(raw);
  if (!isRecord(v)) return null;
  if (v.t === "ping") return { t: "ping" };
  if (v.t === "status" && isPresenceStatus(v.status)) {
    return { t: "status", status: v.status };
  }
  if (v.t === "activity" && typeof v.idle === "boolean") {
    return { t: "activity", idle: v.idle };
  }
  if (
    v.t === "chat_typing" &&
    (v.to === CHAT_ROOM_ALL || (typeof v.to === "number" && Number.isSafeInteger(v.to) && v.to > 0))
  ) {
    return { t: "chat_typing", to: v.to as ChatRoomId | number };
  }
  return null;
}

/**
 * Validate + narrow an untrusted server→client frame (used by the client
 * socket). Returns `null` for anything malformed.
 */
export function parseServerMessage(raw: string): RealtimeServerMessage | null {
  const v = safeJson(raw);
  if (!isRecord(v)) return null;
  switch (v.t) {
    case "pong":
      return { t: "pong" };
    case "status":
      return isPresenceStatus(v.status) ? { t: "status", status: v.status } : null;
    case "presence":
      return isPresenceEntryArray(v.online) ? { t: "presence", online: v.online } : null;
    case "hello":
      return typeof v.user_id === "number" &&
        isPresenceStatus(v.status) &&
        isPresenceEntryArray(v.online)
        ? { t: "hello", user_id: v.user_id, status: v.status, online: v.online }
        : null;
    case "notification":
      return isRecord(v.event)
        ? { t: "notification", event: v.event as unknown as NotificationEvent }
        : null;
    case "chat_message":
      return isChatMessage(v.message) ? { t: "chat_message", message: v.message } : null;
    case "chat_message_update":
      return isChatMessage(v.message)
        ? { t: "chat_message_update", message: v.message }
        : null;
    case "chat_typing":
      return typeof v.from === "number" &&
        typeof v.sender_name === "string" &&
        (v.scope === "dm" || v.scope === "room") &&
        (v.room === null || typeof v.room === "string")
        ? {
            t: "chat_typing",
            from: v.from,
            sender_name: v.sender_name,
            scope: v.scope,
            room: (v.room ?? null) as ChatRoomId | null,
          }
        : null;
    case "chat_read":
      return typeof v.reader_id === "number" && typeof v.last_read_id === "number"
        ? { t: "chat_read", reader_id: v.reader_id, last_read_id: v.last_read_id }
        : null;
    case "chat_reaction":
      return typeof v.message_id === "number" &&
        (v.scope === "dm" || v.scope === "room") &&
        (v.room === null || typeof v.room === "string") &&
        typeof v.sender_id === "number" &&
        (v.recipient_id === null || typeof v.recipient_id === "number") &&
        isReactionSummaryArray(v.reactions)
        ? {
            t: "chat_reaction",
            message_id: v.message_id,
            scope: v.scope,
            room: (v.room ?? null) as ChatRoomId | null,
            sender_id: v.sender_id,
            recipient_id: (v.recipient_id ?? null) as number | null,
            reactions: v.reactions as ChatReactionSummary[],
          }
        : null;
    case "call_invite": {
      const c = v.call;
      if (
        !isRecord(c) ||
        typeof c.call_id !== "string" ||
        typeof c.from_user_id !== "number" ||
        typeof c.from_user_name !== "string" ||
        typeof c.created_at !== "string"
      )
        return null;
      return {
        t: "call_invite",
        call: {
          call_id: c.call_id,
          from_user_id: c.from_user_id,
          from_user_name: c.from_user_name,
          created_at: c.created_at,
        } satisfies CallInvitePayload,
      };
    }
    case "call_state": {
      if (
        typeof v.call_id !== "string" ||
        !(CALL_STATES as readonly string[]).includes(v.state as string)
      )
        return null;
      const state = v.state as CallState;
      // Optional reason — must be a valid CallEndReason if present
      if (
        v.reason !== undefined &&
        !(CALL_END_REASONS as readonly string[]).includes(v.reason as string)
      )
        return null;
      // Optional room — if present, all four coord fields are required
      if (v.room !== undefined) {
        if (
          !isRecord(v.room) ||
          typeof v.room.roomId !== "number" ||
          typeof v.room.participantPin !== "string" ||
          typeof v.room.mediaWsUrl !== "string" ||
          typeof v.room.sipDomain !== "string"
        )
          return null;
      }
      return {
        t: "call_state",
        call_id: v.call_id,
        state,
        ...(v.reason !== undefined ? { reason: v.reason as CallEndReason } : {}),
        ...(v.room !== undefined
          ? {
              room: {
                roomId: (v.room as Record<string, unknown>).roomId as number,
                participantPin: (v.room as Record<string, unknown>).participantPin as string,
                mediaWsUrl: (v.room as Record<string, unknown>).mediaWsUrl as string,
                sipDomain: (v.room as Record<string, unknown>).sipDomain as string,
              } satisfies CallRoomCoords,
            }
          : {}),
      };
    }
    case "call_screen": {
      if (
        typeof v.call_id !== "string" ||
        typeof v.sharing !== "boolean" ||
        typeof v.sharer_session_id !== "number"
      )
        return null;
      return {
        t: "call_screen",
        call_id: v.call_id,
        sharing: v.sharing,
        sharer_session_id: v.sharer_session_id,
      } satisfies CallScreenPayload & { t: "call_screen" };
    }
    case "call_camera": {
      if (
        typeof v.call_id !== "string" ||
        typeof v.sharing !== "boolean" ||
        typeof v.sharer_session_id !== "number"
      )
        return null;
      return {
        t: "call_camera",
        call_id: v.call_id,
        sharing: v.sharing,
        sharer_session_id: v.sharer_session_id,
      } satisfies CallCameraPayload & { t: "call_camera" };
    }
    case "call_roster": {
      if (typeof v.call_id !== "string" || !Array.isArray(v.participants)) return null;
      const participants: CallRosterParticipant[] = [];
      for (const raw of v.participants as unknown[]) {
        const p = raw as Record<string, unknown>;
        if (
          typeof p.user_id !== "number" ||
          typeof p.name !== "string" ||
          (p.state !== "ringing" && p.state !== "joined")
        )
          return null;
        participants.push({ user_id: p.user_id, name: p.name, state: p.state });
      }
      return { t: "call_roster", call_id: v.call_id, participants };
    }
    default:
      return null;
  }
}
